import * as React from 'react';
import { TTypedTFunction, useTranslation } from '@lib/useTypedTranslation';
import { isNil, get, isEmpty } from 'lodash';
import { useLocation } from 'react-router';

import Card from '../../../components/card/card';
import { TabPanel } from '../../../components/tabs/tab-panel';
import { TabPanelHeader } from '../../../components/tabs/tab-panel-header';
import { Tab } from '../../../components/tabs/tab';
import { NoSelectionOverlay } from '../../../components/card/noSelectionOverlay';
import { BatteryDetailsInformation } from './batteryDetailsInformation';
import { BatteryCharts } from './batteryCharts';
import { RoundedLabel } from '../../../components/labels/roundedLabel';
import { useProductsContext } from '../../../context/products';
import { productNames } from '../../app/productsWrapper';

import './styles.css';
import '../../battery-devices/compositions/legacyDetailsCard.css';
import { ISmartBatteryStatus } from 'services/shared/battery/batteryCentricService';

export interface IProps {
  id: string,
  data: any,
  isLoading?: boolean
}

export const getStatusLabel = (battery: ISmartBatteryStatus, t: TTypedTFunction) => {
  const labels = [];

  if (battery.healthStatus === 'red') {
    labels.push(<RoundedLabel key='health' title={t('POOR_HEALTH')} icon="fas fa-times-circle" type="red" />);
  }

  if (battery.averageDischargeStatus === 'red') {
    labels.push(<RoundedLabel key='averageDischarge' title={t("HIGH_DISCHARGE", { ns: 'batteryEssentialsDeviceView' })} icon="fas fa-times-circle" type="red" />);
  }

  if (battery.cycleCountStatus === 'red') {
    labels.push(<RoundedLabel key='cycleCount' title={t('HIGH_CYCLE_COUNT')} icon="fas fa-times-circle" type="red" />);
  }

  if (battery.healthStatus === 'yellow') {
    labels.push(<RoundedLabel key='health' title={t('HEALTH_WARNING')} icon="fas fa-exclamation-triangle" type="yellow" />);
  }

  if (battery.averageDischargeStatus === 'yellow') {
    labels.push(<RoundedLabel key='averageDischarge' title={t("HIGH_DISCHARGE", { ns: 'batteryEssentialsDeviceView' })} icon="fas fa-exclamation-triangle" type="yellow" />);
  }

  if (battery.cycleCountStatus === 'yellow') {
    labels.push(<RoundedLabel key='cycleCount' title={t('CYCLE_COUNT_WARNING')} icon="fas fa-exclamation-triangle" type="yellow" />);
  }

  if (battery.batteryStatus === 'green' && labels.length === 0) {
    labels.push(<RoundedLabel key='noProblem' title={t('NO_PROBLEMS')} icon="fas fa-check" type="green" />);
  }

  return labels;
};

export function BatteryDetailsCard(props: IProps) {
  const { t } = useTranslation();
  const data = isEmpty(props.data?.smartBatteryDetails) ? "-" : props.data.smartBatteryDetails;

  const getBatteryId = (data: any) => {
    const serial = get(data, 'serialNumber');
    return isNil(serial) ? '' : `(${serial})`;
  };

  const underWarranty = data?.underWarranty && <RoundedLabel key='underWarranty' title={t('UNDER_WARRANTY', { ns: 'batteryEssentials' })} icon="fas fa-shield-alt" type="blue" />;
  const statusLabel = getStatusLabel(data, t);

  const batteryId = getBatteryId(data);
  const product = useProductsContext();
  const productTitle = product[productNames.batteryEssentials].title;
  const header = t('NO_SELECTION_HEADER', { title: productTitle, ns: 'batteryEssentials' }) + " - " + t('BATTERY_FIRST_CAPITAL_other', { ns: 'translation' });

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const batteryNotFound = (data === '-' || isEmpty(data)) && params.has('id') && props.isLoading === false;

  return (
    <div className="details-card battery" data-id={String(get(data, 'id')).replace(/\s+/g, '-')}>
      <NoSelectionOverlay
        show={(props.id === '-' || batteryNotFound) && props.isLoading === false}
        icon={batteryNotFound ? null : "fas fa-battery-three-quarters"}
        header={batteryNotFound ? t('BATTERY_NOT_FOUND', { ns: 'batteryEssentials' }) : header}
        noSelectionText={batteryNotFound ? t('BATTERY_NOT_FOUND_BODY', { ns: 'batteryEssentials' }) : [t('NO_SELECTION_BODY_UPPER', { ns: 'batteryEssentials' }), t('NO_SELECTION_BODY_LOWER', { ns: 'batteryEssentials' })]}
        opaque={true}
      />
      <Card noPadding={true}>
        <TabPanel>
          <TabPanelHeader>
            <div className="details-tab-header">
              <span className="selection-header">
                {t('BATTERY_STATUS_HEADER', { ns: 'batteryEssentials' })} {batteryId}
              </span>
              {statusLabel}
              {underWarranty}
            </div>
          </TabPanelHeader>
          <Tab id="Information" title={t('INFORMATION')}>
            <BatteryDetailsInformation
              key={'details' + props.id}
              id={props.id}
              data={data}
            />
          </Tab>
          <Tab id="ChargeLevel" title={t('CHARGE_LEVEL')}>
            <BatteryCharts
              key={'details' + props.id}
              id={props.id}
              data={props.data?.smartBatteryLast30DaysDischargeTrend}
              isLoading={props?.isLoading}
            />
          </Tab>
        </TabPanel>
      </Card>
    </div>
  );
}
